/**
 * Staff sign-in with a mobile number (backend staffauth). Only a number the person turned on from their own profile
 * can sign in; the answer to "is this number turned on" comes only after the code.
 */
import { fetchApi } from '../client';
import { captureStaffSession } from '../sessionToken';

export interface StaffCodeSent { challengeId: string; expiresInSeconds: number; resendAfterSeconds: number }

/** One account a proved number may open, when it opens several. */
export interface StaffAccountChoice { userId: string; name: string | null; role: string | null; clinicName: string | null }

export interface StaffMobileStatus { enabled: boolean; last4: string | null; verifiedAt: string | null; lastSignInAt: string | null }

const post = (body?: unknown) => ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const staffMobileSignInModule = {
  requestCode: (mobile: string) => fetchApi<StaffCodeSent>('/api/auth/staff-otp/request', post({ mobile })),

  /**
   * A session (or mfaRequired) like a password login, or `{ status: 'CHOOSE_ACCOUNT', selectionToken, accounts }`
   * when the number opens several of the person's accounts.
   */
  verify: async (challengeId: string, mobile: string, code: string) => {
    const response = await fetchApi<unknown>('/api/auth/staff-otp/verify', post({ challengeId, mobile, code }));
    captureStaffSession(response);
    return response;
  },

  choose: async (selectionToken: string, userId: string) => {
    const response = await fetchApi<unknown>('/api/auth/staff-otp/choose', post({ selectionToken, userId }));
    captureStaffSession(response);
    return response;
  },

  // ── The signed-in person's own number ──
  status: () => fetchApi<StaffMobileStatus>('/api/auth/mobile-sign-in'),
  requestBindCode: (mobile: string) => fetchApi<StaffCodeSent>('/api/auth/mobile-sign-in/request-code', post({ mobile })),
  verifyBind: (challengeId: string, mobile: string, code: string) =>
    fetchApi<StaffMobileStatus>('/api/auth/mobile-sign-in/verify', post({ challengeId, mobile, code })),
  turnOff: () => fetchApi<StaffMobileStatus>('/api/auth/mobile-sign-in', { method: 'DELETE' }),
};

/** The CHOOSE_ACCOUNT answer, if this response is one. */
export function accountChoiceOf(response: { data?: unknown }): { selectionToken: string; accounts: StaffAccountChoice[] } | null {
  const outer = response?.data as { status?: string; selectionToken?: string; accounts?: StaffAccountChoice[]; data?: unknown } | undefined;
  const d = (outer && 'status' in outer ? outer : (outer?.data as typeof outer)) ?? undefined;
  if (d?.status === 'CHOOSE_ACCOUNT' && d.selectionToken && Array.isArray(d.accounts)) {
    return { selectionToken: d.selectionToken, accounts: d.accounts };
  }
  return null;
}
